import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  size: string;
  color: string;
  quantity: number;
}

export default function PanierPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cart, setCart] = useState<CartItem[]>([]);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCart(savedCart);
  };

  const updateQuantity = (index: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    const updatedCart = [...cart];
    updatedCart[index].quantity = newQuantity;
    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const removeItem = (index: number) => {
    const updatedCart = cart.filter((_, i) => i !== index);
    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = 7;
  const grandTotal = total + shipping;

  const proceedToCheckout = () => {
    if (cart.length > 0) {
      navigate('/commande');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12 pt-24 md:pt-28">
        <h1 className="text-3xl md:text-4xl font-bold mb-6 md:mb-8">{t('panier.title')}</h1>

        {cart.length === 0 ? (
          <div className="text-center py-12 md:py-16">
            <i className="ri-shopping-cart-line text-5xl md:text-6xl text-gray-300 mb-4"></i>
            <h2 className="text-xl md:text-2xl font-semibold mb-2">{t('panier.emptyCart')}</h2>
            <p className="text-gray-600 mb-6 md:mb-8 text-sm">{t('panier.emptyCartDesc')}</p>
            <a
              href="/boutique"
              className="inline-block bg-black text-white px-6 py-3 md:px-8 rounded-md hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm font-bold"
            >
              {t('panier.continueShopping')}
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-3 md:space-y-4">
              {cart.map((item, index) => (
                <div
                  key={index}
                  className="flex gap-3 md:gap-4 p-3 md:p-4 border border-gray-200 rounded-lg"
                >
                  <div className="w-20 h-28 md:w-24 md:h-32 flex-shrink-0 overflow-hidden rounded-md bg-gray-100">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold mb-1 text-sm leading-tight">{item.name}</h3>
                    <p className="text-xs text-gray-600 mb-0.5">
                      {t('panier.size')}: {item.size}
                    </p>
                    <p className="text-xs text-gray-600 mb-2">
                      {t('panier.color')}: {item.color}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 md:gap-2">
                        <button
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-50 cursor-pointer"
                        >
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <span className="w-7 text-center text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                          className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-50 cursor-pointer"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(index)}
                        className="text-red-400 hover:text-red-600 cursor-pointer p-1"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-sm">{item.price * item.quantity} TND</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 p-4 md:p-6 rounded-lg lg:sticky lg:top-24">
                <h2 className="text-lg md:text-xl font-bold mb-4 md:mb-6">{t('panier.summary')}</h2>
                <div className="space-y-3 mb-4 md:mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('panier.subtotal')}</span>
                    <span className="font-semibold">{total} TND</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('panier.shipping')}</span>
                    <span className="font-semibold">7 DT</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-base md:text-lg">
                    <span className="font-bold">{t('panier.total')}</span>
                    <span className="font-bold">{grandTotal} TND</span>
                  </div>
                </div>
                <button
                  onClick={proceedToCheckout}
                  className="w-full bg-black text-white py-3 rounded-md hover:bg-gray-800 transition-colors mb-3 whitespace-nowrap cursor-pointer text-sm font-bold tracking-widest"
                >
                  {t('panier.placeOrder')}
                </button>
                <a
                  href="/boutique"
                  className="block text-center text-gray-600 hover:text-[#F2A7C3] transition-colors cursor-pointer text-sm"
                >
                  {t('panier.continueShopping')}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}